import { fetchWithCurrentSessionJwt } from '../auth/fetchWithCurrentSessionJwt';
import { LLM_SERVICE_BASE_URL } from '../config/config';
import FetchError from '../http/FetchError';
import { captureException } from '../logger/Logger';

interface LlmServiceFetcherArgs {
    endpoint: string;
    body?: BodyInit;
    method?: 'DELETE' | 'GET' | 'PATCH' | 'POST' | 'PUT';
    signal?: AbortSignal;
}

/**
 * Raw response for custom llm-service routes (outside Mastra's `/api` prefix).
 * Prefer `useMastraClient()` for native Mastra SDK endpoints.
 * 4xx failures throw but are not reported to Sentry (via captureException).
 */
export const llmServiceFetcherResponse = async ({
    endpoint,
    body,
    method = 'GET',
    signal,
}: LlmServiceFetcherArgs): Promise<Response> => {
    const response = await fetchWithCurrentSessionJwt(
        `${LLM_SERVICE_BASE_URL}${endpoint}`,
        {
            method,
            signal,
            headers: {
                'Content-Type': 'application/json',
            },
            ...(method !== 'GET' && body != null ? { body } : {}),
        },
    );

    if (!response.ok) {
        let json: object = {};
        try {
            const parsed: unknown = await response.json();
            if (typeof parsed === 'object' && parsed != null) {
                json = parsed;
            }
        } catch {
            // Non-JSON error bodies are fine; status is enough.
        }

        const message =
            'error' in json && typeof json.error === 'string'
                ? json.error
                : 'message' in json && typeof json.message === 'string'
                  ? json.message
                  : `HTTP error! status: ${String(response.status)}`;

        const error = new FetchError(message, response, json, {
            endpoint,
            method,
        });
        captureException(error);
        throw error;
    }

    return response;
};

export const llmServiceFetcher = async <Data = unknown>(
    args: LlmServiceFetcherArgs,
): Promise<Data> => {
    const response = await llmServiceFetcherResponse(args);
    const data: unknown = await response.json();
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- generic JSON body
    return data as Data;
};
